
import { FacilityRequestService } from 'src/app/services/facility-request/facility-request.service';
import { Injectable } from '@angular/core';
import { ISmartActionContext, ILazyComponentHandler, ISmartActionMetadata, SmartActionContext, SmartActionContextItem } from '../../models/smart-actions.models';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { IFacilityRequestConfig, IFacilityRequestForSmartAction, IFacilityRequestInlineManagement, IFacilityRequestListItem } from 'src/app/models/facility-request';
import { MessageSeverity } from '../alert.service';


export const noteSmartAction = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-notes`,
    displayName: 'Notes',
    description: 'Show notes',
    target: service,
    actionMetadata: listItem,

    command: async (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction as SmartActionContextItem;
        const handler = context.lazyComponentHandler as ILazyComponentHandler;
        const target = sourceAction.target as FacilityRequestService;

        const currentFacilityRequestConfig = sourceAction.actionMetadata.currentFacilityRequestConfig as IFacilityRequestConfig;

        try {
            await target.getFacilityRequest(currentFacilityRequestConfig);
            handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_FACILITYREQUESTNOTEDIALOG, context);
        } catch (error) {
            target.alertService.showStickyMessage('Error', 'Error retrieving facility request', MessageSeverity.error, error);
        }

    }
} as SmartActionContextItem);

export const noteWithConfirmationSmartAction = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-noteswithconfirmation`,
    displayName: 'Show notes',
    description: 'Notes...',
    target: service,
    handler: service.smartActionService,
    command: async (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction;
        const target = sourceAction.target as FacilityRequestService;
        const handler = context.lazyComponentHandler as ILazyComponentHandler;
        handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_FACILITYREQUESTNOTEDIALOG, context);
    },
    confirmation: {
        id: 'facilityrequest-cancel',
        title: listItem.Member ? `Confirm Cancelation for ${listItem.Member.FirstName} ${listItem.Member.LastName}` : 'Confirm Cancelation',
        details: `Are you sure you want to cancel the facility request to ${listItem.Facility.Name}?`,
        type: 'dialog',
        icon: 'fal fa-exclamation-triagle',
        okLabel: 'Yes',
        closeLabel: 'Cancel'
    },
} as SmartActionContextItem);

export const cancelSmartAction = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-cancel`,
    displayName: 'Cancel',
    description: 'Cancel facility request',
    target: service,
    handler: service.smartActionService,
    command: (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction;
        const target = (sourceAction.target as FacilityRequestService);
        return target.router.navigate([], { queryParams: { openDialog: true }, queryParamsHandling: 'merge', relativeTo: target.route });
    },
    confirmation: {
        id: 'facilityrequest-cancel',
        title: `Confirm Cancelation for ${listItem.Member.FirstName} ${listItem.Member.LastName}`,
        details: `Are you sure you want to cancel the facility request to ${listItem.Facility.Name}?`,
        type: 'dialog',
        icon: 'fal fa-exclamation-triagle',
        okLabel: 'Yes',
        closeLabel: 'Cancel'
    },
} as SmartActionContextItem);

export const reSubmitSmartAction = (service: FacilityRequestService, item: IFacilityRequestForSmartAction, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-resubmit`,
    displayName: 'Re-Submit',
    description: 'Re-submit facility request',
    target: service,
    handler: service.smartActionService,
    command: (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction;
        const target = (sourceAction.target as FacilityRequestService);
        return target.router.navigate([], { queryParams: { openDialog: true }, queryParamsHandling: 'merge', relativeTo: target.route });
    },
    confirmation: {
        id: 'facilityrequest-resubmit',
        title: listItem.Member ? `Confirm re-submit for ${listItem.Member.FirstName} ${listItem.Member.LastName}` : 'Confirm re-submit',
        details: `Are you sure you want to re-submit the facility request ${listItem.Facility.Name}?`,
        type: 'dialog',
        icon: 'fal fa-exclamation-triagle',
        okLabel: 'Yes',
        closeLabel: 'Cancel'
    },
} as SmartActionContextItem);

export const completeSmartAction = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-complete`,
    displayName: 'Complete',
    description: `Complete facility request to ${listItem.Facility.Name}`,
    target: service,
    handler: service.smartActionService,
    actionMetadata: {
        currentFacilityRequestConfig: {
            memberId: listItem.Member.Id && listItem.Member.Id.toString(),
            base64: listItem.Member.IdBase64,
            tenantId: listItem.TenantName,
            facilityRequestExternalId: listItem.FacilityRequestExternalId,
        } as IFacilityRequestConfig,
    } as IFacilityRequestInlineManagement,
    command: async (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction as SmartActionContextItem;
        const handler = context.lazyComponentHandler as ILazyComponentHandler;
        const target = sourceAction.target as FacilityRequestService;
        const currentFacilityRequestConfig = sourceAction.actionMetadata.currentFacilityRequestConfig as IFacilityRequestConfig;

        try {
            await target.getFacilityRequest(currentFacilityRequestConfig);
            handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_FACILITYREQUESTCOMPLETE, context);

        } catch (error) {
            target.alertService.showStickyMessage('Error', 'Error retrieving facility request', MessageSeverity.error, error);
        }

    }
} as SmartActionContextItem);

export const openMemberSmartAction = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => new SmartActionContextItem({
    id: `facilityrequest-openmember`,
    displayName: 'Open Member',
    description: 'View member details in a new tab',
    target: service,
    handler: service.smartActionService,
    command: async () => {
        const memberId = listItem.Member?.IdBase64 || listItem.Member?.Id?.toString();
        if (memberId) {
            const url = `/member/${memberId}`; // adjust the url to navigate to member dashboard
            window.open(url);
        } else {
            service.alertService.showStickyMessage('Error', 'Member information is missing or invalid', MessageSeverity.error);
        }
    }
} as SmartActionContextItem);

@Injectable({
    providedIn: 'root'
})
export class FacilityRequestSmartActionsService {

    forDefaultStage = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => {

        const actions: SmartActionContextItem[] = [];

        const currentUrl = service.router.url;
        const isInsideMemberContext = currentUrl.includes('/member/');

        if (!isInsideMemberContext) {
            actions.push(openMemberSmartAction(service, listItem));
        }

        actions.push(noteSmartAction(service, listItem));
        // actions.push(noteWithConfirmationSmartAction(service, item));
        // actions.push(cancelSmartAction(service, item));
        // actions.push(reSubmitSmartAction(service, item));
        actions.push(completeSmartAction(service, listItem));

        return new SmartActionContext( {
            id: `facilityrequest-addnote`,
            displayName: 'Facility Request Actions',
            description: 'Facility request actions',
            actions
        } as ISmartActionContext);
    }

    forNotes = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => {

        const actions: SmartActionContextItem[] = [];

        actions.push(completeSmartAction(service, listItem));

        return new SmartActionContext( {
            id: `facilityrequest-addnote`,
            displayName: 'Facility Request Actions',
            description: 'Facility request actions',
            actions
        } as ISmartActionContext);
    }

    forInstance = (service: FacilityRequestService, listItem: IFacilityRequestListItem) => {

        const actions: SmartActionContextItem[] = [];
        actions.push(completeSmartAction(service, listItem));

        return new SmartActionContext( {
            id: `facilityrequest-forinstance`,
            displayName: 'Facility Request Actions',
            description: 'Facility request actions',
            actions
        } as ISmartActionContext);
    }
}
